import Modal from "../../../../../ui/Modal/Modal";
import PlanTile, { PlanTileProps } from "./PlanTile/PlanTile";
import styles from "./ChangePlanModal.module.scss";
import {
  BillingTier,
  PlanTerm,
} from "../../../../../__generated__/gql/graphql";
import Button from "../../../../../ui/Button/Button";
import { useCallback, useState } from "react";
import { graphql } from "../../../../../__generated__/gql";
import { useMutation } from "urql";
import useUser from "../../../../../providers/UserProvider/useUser";
import { ChatCircle, LinkExternal } from "../../../../../ui/Icon/Icon";
import { billingTierPrice, billingTierRank } from "../../../../../util/billing";
import { useDisclosure } from "../../../../../ui/util/useDisclosure";
import DowngradePlanModal from "./DowngradePlanModal";
import CancelPlanModal from "./CancelPlanModal";
import ToggleGroup from "../../../../../ui/ToggleGroup/ToggleGroup";
import Badge from "../../../../../ui/Badge/Badge";

const CreateSubscriptionCheckoutMutation = graphql(/* GraphQL */ `
  mutation createSubscriptionCheckout(
    $input: CreateSubscriptionCheckoutInput!
  ) {
    createSubscriptionCheckout(input: $input) {
      checkoutUrl
    }
  }
`);

const getPlans = (
  planTerm: string,
): Pick<PlanTileProps, "price" | "billingTier" | "features">[] => [
  {
    price:
      planTerm === "monthly"
        ? billingTierPrice.monthly[BillingTier.Starter]
        : billingTierPrice.yearly[BillingTier.Starter],
    billingTier: BillingTier.Starter,
    features: [
      "10 Articles per month",
      "$8.5 per extra article",
      "250 linked website pages",
      "1 free regeneration per article",
      "Live chat support",
    ],
  },
  {
    price:
      planTerm === "monthly"
        ? billingTierPrice.monthly[BillingTier.Pro]
        : billingTierPrice.yearly[BillingTier.Pro],
    billingTier: BillingTier.Pro,
    features: [
      "40 Articles per month",
      "$7 per extra article",
      "1000 linked website pages",
      "1 free regenerations per article",
      "Live chat support",
    ],
  },
  {
    price:
      planTerm === "monthly"
        ? billingTierPrice.monthly[BillingTier.Scale]
        : billingTierPrice.yearly[BillingTier.Scale],
    billingTier: BillingTier.Scale,
    features: [
      "150 Articles per month",
      "$6 per extra article",
      "5000 linked website pages",
      "2 free regenerations per article",
      "Strategic support",
    ],
  },
];

function InternalChangePlanModal({ onClose }: { onClose: () => void }) {
  const [planTerm, setPlanTerm] = useState("monthly");
  const [, createSubscriptionCheckout] = useMutation(
    CreateSubscriptionCheckoutMutation,
  );
  const [cancelPlanModalOpened, cancelPlanModal] = useDisclosure();
  const [downgradePlanModalOpened, downgradePlanModal] = useDisclosure();
  const { billingInfo } = useUser();
  const [selectedBillingTier, setSelectedBillingTier] = useState<
    BillingTier | undefined
  >(undefined);

  const onSubmit = useCallback(
    async (billingTier: BillingTier) => {
      if (
        billingTierRank[billingTier] < billingTierRank[billingInfo.billingTier]
      ) {
        setSelectedBillingTier(billingTier);
        downgradePlanModal.open();
      } else {
        const checkoutSession = await createSubscriptionCheckout({
          input: {
            billingTier,
            planTerm:
              planTerm === "monthly" ? PlanTerm.Monthly : PlanTerm.Yearly,
            returnUrl: window.location.href,
            referralCode:
              "tolt_referral" in window &&
              typeof window.tolt_referral === "string"
                ? window.tolt_referral
                : null,
          },
        });

        if (checkoutSession.data?.createSubscriptionCheckout.checkoutUrl) {
          window.location.href =
            checkoutSession.data.createSubscriptionCheckout.checkoutUrl;
        }
      }
    },
    [
      billingInfo.billingTier,
      createSubscriptionCheckout,
      downgradePlanModal,
      planTerm,
    ],
  );

  return (
    <div className={styles.container}>
      <div className={styles.plans}>
        {getPlans(planTerm).map((plan) => (
          <PlanTile
            key={plan.billingTier}
            planTerm={
              planTerm === "yearly" ? PlanTerm.Yearly : PlanTerm.Monthly
            }
            price={plan.price}
            billingTier={plan.billingTier}
            features={plan.features}
            currentBillingTier={billingInfo.billingTier}
            checked={false}
            onSubmit={async () => {
              if (plan.price === Infinity) {
                window.Intercom("showNewMessage");
              } else {
                await onSubmit(plan.billingTier);
              }
            }}
          />
        ))}
      </div>
      <div className={styles.footer}>
        <Button
          className={styles.chatButton}
          leftIcon={<ChatCircle size="medium" />}
          variant="outline"
          size="small"
          onClick={() => window.Intercom("showNewMessage")}
        >
          Chat to us
        </Button>

        {billingInfo.billingTier !== BillingTier.Free &&
        billingInfo.downgradeBillingTier !== BillingTier.Free ? (
          <Button variant="outline" size="small" onClick={cancelPlanModal.open}>
            Downgrade to free
          </Button>
        ) : (
          <Button
            leftIcon={<LinkExternal size="medium" />}
            size="small"
            variant="outline"
            onClick={() =>
              window.open("https://www.macawhq.com/pricing", "_blank")?.focus()
            }
          >
            View full pricing
          </Button>
        )}

        <ToggleGroup
          selectedItem={planTerm}
          setSelectedItem={setPlanTerm}
          className={styles.buttonGroup}
          size="small"
          items={[
            {
              id: "yearly",
              name: (
                <span className={styles.yearlyButton}>
                  Yearly
                  <Badge showDot={false} color="success">
                    Save 34%
                  </Badge>
                </span>
              ),
            },
            { id: "monthly", name: "Monthly" },
          ]}
        />
        <CancelPlanModal
          isOpen={cancelPlanModalOpened}
          onClose={cancelPlanModal.close}
        />
      </div>
      <DowngradePlanModal
        isOpen={downgradePlanModalOpened}
        onClose={downgradePlanModal.close}
        billingTier={selectedBillingTier ?? BillingTier.Free}
        onSubmit={() => {
          setSelectedBillingTier(undefined);
          onClose();
        }}
      />
    </div>
  );
}

function ChangePlanModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      title="Select plan"
      classNames={{ body: styles.body }}
    >
      <InternalChangePlanModal onClose={onClose} />
    </Modal>
  );
}

export default ChangePlanModal;
